import { IUserDto, IUserInfoDto } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import { FormInstance } from "antd/es/form";
import * as React from "react";
import { UserService } from "../../../../services/user-service";
import { IWithHistoryProp, withHistory } from "../../../../shared/hoc/with-history";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { SaveStatus } from "../../../../shared/utility/save-status";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KModalStepBody from "../../../ui-kit/k-modal/k-modal-step-body";
import { type IKuikaButtonStatusLabels, type KuikaButtonStatus } from "../../../ui-kit/kuika-button/kuika-button";
import KuikaInfoBox from "../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaModalIconWrapper, {
  type KuikaModalIconIntent
} from "../../../ui-kit/kuika-modal-icon-wrapper/kuika-modal-icon-wrapper";
import KuikaOtpInput, { type KuikaOtpInputStatus } from "../../../ui-kit/kuika-otp-input/kuika-otp-input";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaH5 from "../../../ui-kit/typography/H5/H5";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import "./transfer-ownership-modal.scss";

/** The `id` the footer's submit button targets — see `KModalFooter.submitForm`. */
const FORM_ID: string = "transfer-ownership-form";

/** The verification code is six digits. */
const CODE_LENGTH: number = 6;

/** What the two steps' commits are doing, so the button says it rather than "Saving". */
const SEND_CODE_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Sending code" };
const TRANSFER_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Transferring", success: "Transferred" };

export interface IState {
  saveStatus: KuikaButtonStatus;
  isSubmitButtonDisabled: boolean;
  step: EStep;
  userToTransfer: IUserDto;
  verificationCode: string;
  /** `error` draws the code cells red and shakes them once; cleared by the next edit. */
  codeStatus: KuikaOtpInputStatus;
}

export interface IProps {
  /** The current owner. `users.tsx` holds it as either shape, so the prop takes both. */
  user: IUserDto | IUserInfoDto;
  userList: IUserDto[];
  isModalOpen: boolean;
  closeModal: (shouldRefresh: boolean) => void;
}

enum EStep {
  FIRST = 1,
  SECOND = 2
}

class TransferOwnershipModal extends React.PureComponent<IProps & IWithHistoryProp, IState> {
  private formRef = React.createRef<FormInstance>();

  private otpRef: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

  /** Where the commit stands. Owned by `SaveStatus`, never set directly. */
  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: IProps & IWithHistoryProp) {
    super(props);

    this.state = {
      isSubmitButtonDisabled: true,
      saveStatus: "idle",
      step: EStep.FIRST,
      userToTransfer: {} as IUserDto,
      verificationCode: "",
      codeStatus: "default"
    };
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  saveUser = async (values: { userName?: string }): Promise<void> => {
    // A second submit while the commit runs or its ✓ is held (the button is live again during the
    // hold, and Enter submits the form regardless) would call the service twice and cancel the close.
    if (this.saveStatus.current === "loading" || this.saveStatus.current === "success") return;
    this.saveStatus.start();
    if (this.state.step === EStep.FIRST) {
      const userToTransfer = this.props.userList.find((user) => user.username === values.userName);
      if (!userToTransfer) {
        this.saveStatus.reset();
        return;
      }
      try {
        const res = await UserService.InitiateWorkspaceOwnershipTransfer(userToTransfer.id);
        if (res) {
          // Reaching the code step IS this step's confirmation — the dialog grows into it — so the
          // button goes straight back to idle rather than flashing a ✓ nobody is looking at.
          this.saveStatus.reset();
          this.setState({ userToTransfer, step: EStep.SECOND, isSubmitButtonDisabled: true });
          this.focusCode();
        } else {
          this.saveStatus.fail();
        }
      } catch (error) {
        KMainFunctions.exceptionHandler(error);
        this.saveStatus.fail();
      }
    } else {
      if (this.state.verificationCode.length !== CODE_LENGTH) {
        KMainFunctions.displayWarningNotification("Please enter the verification code completely");
        this.saveStatus.reset();
        return;
      }
      try {
        const res = await UserService.ConfirmWorkspaceOwnershipTransfer(this.state.verificationCode);
        if (res && res.status === 200) {
          // The transfer leaves no row behind — the whole workspace reloads as a member — so the
          // toast stays. The ✓ goes up first and the reload waits behind it.
          KMainFunctions.displaySuccessNotification("Ownership transfer confirmed successfully");
          this.saveStatus.succeed((): void => {
            this.props.closeModal(true);
            this.props.navigator.gotoRecent();
            window.location.reload();
          });
          return;
        }
        this.rejectCode();
      } catch (error) {
        KMainFunctions.exceptionHandler(error);
        this.rejectCode();
      }
    }
  };

  /**
   * A code the server would not take: the button says so, the cells shake and the caret goes back to
   * the field. The code itself stays, as it did in the six boxes, so one digit can be corrected.
   */
  private rejectCode = (): void => {
    this.saveStatus.fail();
    this.setState({ isSubmitButtonDisabled: true, codeStatus: "error" });
    this.focusCode();
  };

  /**
   * `users.tsx` keeps this dialog mounted between openings, so closing it has to put it back to the
   * start: otherwise the next opening lands on the code step with the last code still in the field
   * (the code is this component's state, not a form field the dialog destroys) and the submit guard
   * of an abandoned commit still latched.
   */
  handleClickCancel = (): void => {
    // Closing during the ✓ hold would cancel the close the success already scheduled (and with it
    // the reload/refresh that follows), so it is swallowed; the dialog leaves on its own a moment later.
    if (this.saveStatus.current === "success") return;
    this.saveStatus.reset();
    this.setState({
      step: EStep.FIRST,
      userToTransfer: {} as IUserDto,
      verificationCode: "",
      codeStatus: "default",
      isSubmitButtonDisabled: true
    });
    this.props.closeModal(false);
  };

  /** The code is one field now, so the caret always lands in the same place. */
  private focusCode = (): void => {
    this.otpRef.current?.focus();
  };

  private getModalTitle = (): string => {
    if (this.state.step === EStep.FIRST) {
      return "Change workspace owner";
    }
    return "Verify transfer of ownership";
  };

  /**
   * Every edit clears the rejected-code mark, which is also what lets the cells shake again on the
   * next refusal. The field itself keeps non-digits out, so there is no key filter here any more.
   */
  handleVerificationCodeChange = (verificationCode: string): void => {
    this.setState({
      isSubmitButtonDisabled: verificationCode.length !== CODE_LENGTH,
      verificationCode,
      codeStatus: "default"
    });
  };

  checkDisabledStatus = (): void => {
    if (this.state.step === EStep.FIRST) {
      const values = this.formRef.current?.getFieldsValue();
      this.setState({ isSubmitButtonDisabled: !values?.userName });
    }
  };

  /** Members who have accepted their invitation, minus the current owner. */
  private getTransferableMembers = (): KuikaSelectboxOption[] =>
    (this.props.userList ?? [])
      .filter((user) => user.invitationStatus === null && user.id !== this.props.user.id)
      .map((user): KuikaSelectboxOption => ({
        key: user.id,
        value: user.username,
        label: user.username
      }));

  private renderPickStep(): React.ReactNode {
    const intent: KuikaModalIconIntent = "neutral";
    return (
      <div className="kuika-transfer-ownership__step">
        <div className="kuika-transfer-ownership__header">
          <KuikaModalIconWrapper iconName="users-group" intent={intent} />
          <KuikaH5>{this.getModalTitle()}</KuikaH5>
        </div>
        <KuikaText className="kuika-transfer-ownership__lede">Select the user who will own the workspace.</KuikaText>
        <div className="kuika-transfer-ownership__form-item">
          <KuikaSmallText className="kuika-transfer-ownership__label">Search member</KuikaSmallText>
          <Form.Item name="userName" rules={[{ required: true, message: "Please input a user name!" }]}>
            <KuikaSelectbox
              data-testid="transferOwnershipMemberSelect"
              placeholder="Write email address or full name..."
              options={this.getTransferableMembers()}
              searchable
              size="md"
            />
          </Form.Item>
        </div>
      </div>
    );
  }

  private renderVerifyStep(): React.ReactNode {
    const intent: KuikaModalIconIntent = "destructive";
    return (
      <div className="kuika-transfer-ownership__step">
        <div className="kuika-transfer-ownership__header">
          <KuikaModalIconWrapper iconName="lock" intent={intent} />
          <KuikaH5>{this.getModalTitle()}</KuikaH5>
        </div>
        <KuikaSmallText className="kuika-transfer-ownership__label kuika-transfer-ownership__label--centered">
          Verify code
        </KuikaSmallText>

        {/*
          One field, six cells — outside the antd Form, because the code is this component's own
          state and the form only carries the member it was picked for. Submitting is still the
          footer button's job: the code completing does not confirm the transfer by itself.
        */}
        <div className="kuika-transfer-ownership__code" data-testid="transferOwnershipCode">
          <KuikaOtpInput
            ref={this.otpRef}
            value={this.state.verificationCode}
            onChange={this.handleVerificationCodeChange}
            length={CODE_LENGTH}
            status={this.state.codeStatus}
            ariaLabel="Verification code"
            dataTestId="transferOwnershipCodeInput"
          />
        </div>

        <KuikaText className="kuika-transfer-ownership__explainer">
          For Workspace ownership change, confirm the transaction with the verification code sent to your e-mail
          address.
        </KuikaText>

        <KuikaInfoBox
          color="yellow"
          leftUnit={<KuikaSvg type="common" name="circle-warning" width={16} height={16} />}
          rightUnit={
            <KuikaText>
              After approval, your authority will be reduced to &quot;member&quot; status. The change of ownership is
              irreversible.
            </KuikaText>
          }
        />
      </div>
    );
  }

  render() {
    const isVerifying: boolean = this.state.step === EStep.SECOND;

    return (
      <KModal
        showModal={this.props.isModalOpen}
        closeModal={this.handleClickCancel}
        header={this.getModalTitle()}
        footer={
          <KModalFooter
            submitLabel={isVerifying ? "Confirm ownership transfer" : "Next"}
            submitTestId="transferOwnershipSubmitButton"
            submitForm={FORM_ID}
            submitTone={isVerifying ? "danger" : "default"}
            status={this.state.saveStatus}
            statusLabels={isVerifying ? TRANSFER_STATUS_LABELS : SEND_CODE_STATUS_LABELS}
            disabled={this.state.isSubmitButtonDisabled}
          />
        }
        width={420}
        centered
        isLoading={this.state.saveStatus === "loading"}
      >
        <Form
          id={FORM_ID}
          name={FORM_ID}
          onFinish={this.saveUser}
          onFieldsChange={this.checkDisabledStatus}
          ref={this.formRef}
        >
          {/*
            The step body, not the whole KModal: the morph has to sit INSIDE the form, or the step
            leaving would be a second <form id="transfer-ownership-form"> and the footer's submit
            button — which finds its form by that id — would aim at the one on its way out.
          */}
          <KModalStepBody stepKey={this.state.step}>
            <div className="kuika-transfer-ownership">
              {isVerifying ? this.renderVerifyStep() : this.renderPickStep()}
            </div>
          </KModalStepBody>
        </Form>
      </KModal>
    );
  }
}

const transferOwnershipModal = withHistory<IProps>(TransferOwnershipModal);
export { transferOwnershipModal as TransferOwnershipModal };
